import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { ProductCard } from "../components/ProductCard";
import { MaskLines, Reveal } from "../components/Reveal";
import { PRODUCTS } from "../data/products";
import { useSEO } from "../lib/useSEO";

const FILTERS = [
  { key: "All", label: "All Products" },
  { key: "Economy", label: "Economy" },
  { key: "Premium", label: "Premium" },
  { key: "Assorted", label: "Assorted Pouches" },
];

export default function Products() {
  useSEO(
    "Incense Sticks & Fragrances | SK Fragrances Bangalore",
    "Explore the SK Fragrances collection of 41 incense fragrances across Economy, Premium and Assorted Pouches — crafted in Bangalore since 1980."
  );
  const [params, setParams] = useSearchParams();
  const initial = params.get("category") || "All";
  const [active, setActive] = useState(FILTERS.some((f) => f.key === initial) ? initial : "All");
  const [query, setQuery] = useState("");

  useEffect(() => {
    const c = params.get("category");
    if (c && FILTERS.some((f) => f.key === c)) setActive(c);
  }, [params]);

  const selectFilter = (key) => {
    setActive(key);
    if (key === "All") setParams({});
    else setParams({ category: key });
  };

  const filtered = useMemo(() => {
    return PRODUCTS.filter((p) => (active === "All" || p.category === active) &&
      (p.name.toLowerCase().includes(query.toLowerCase()) || p.family.includes(query.toLowerCase())));
  }, [active, query]);

  return (
    <div data-testid="products-page" className="bg-ivory">
      {/* HERO */}
      <section className="pt-40 md:pt-48 pb-16 bg-cream">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <p className="text-sm tracking-[0.3em] text-gold mb-6">THE SK COLLECTION</p>
          <MaskLines lines={["The SK Collection"]} className="font-serif text-6xl md:text-8xl text-charcoal" />
          <Reveal delay={0.3}>
            <p className="mt-6 max-w-xl text-charcoal/65 leading-relaxed">
              Explore our collection of incense fragrances, crafted for everyday rituals, personal spaces and moments of calm.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FILTER BAR */}
      <section className="sticky top-[64px] z-30 bg-ivory/90 backdrop-blur-xl border-y border-charcoal/10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-4 flex flex-col md:flex-row gap-4 md:items-center justify-between">
          {/* desktop filters */}
          <div className="hidden md:flex items-center gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`filter-${f.key}`}
                onClick={() => selectFilter(f.key)}
                className={`px-5 py-2 text-sm tracking-wide transition-colors duration-300 ${
                  active === f.key ? "bg-charcoal text-ivory" : "text-charcoal/70 hover:text-charcoal border border-transparent"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          {/* mobile dropdown */}
          <select
            data-testid="filter-mobile"
            value={active}
            onChange={(e) => selectFilter(e.target.value)}
            className="md:hidden bg-transparent border border-charcoal/25 px-4 py-3 text-sm text-charcoal outline-none"
          >
            {FILTERS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>

          <div className="relative w-full md:w-64">
            <Search size={16} className="absolute left-0 top-1/2 -translate-y-1/2 text-charcoal/40" />
            <input
              data-testid="product-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search fragrance..."
              className="w-full bg-transparent border-b border-charcoal/25 focus:border-gold outline-none pl-6 py-2 text-sm text-charcoal placeholder:text-charcoal/40 transition-colors"
            />
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="py-14 md:py-20">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <p className="text-xs tracking-[0.2em] text-charcoal/50 mb-8" data-testid="products-count">
            {filtered.length} {filtered.length === 1 ? "FRAGRANCE" : "FRAGRANCES"}
          </p>
          {filtered.length === 0 ? (
            <p className="font-serif text-3xl text-charcoal/60 py-20 text-center">No fragrances found. Try another search.</p>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {filtered.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
